var _excluded = ["olLayer", "canDraw", "drawShape", "canModify", "drawStyle", "allowMapMarker", "markerStyle", "markerCoordinates", "measurementBaseStyles", "measurementAreaStyle", "measurementLineStyle", "showLineMeasurements", "showAreaMeasurements", "canSnap", "onChange", "onDrawEnd", "onDrawStart", "onModifyEnd", "onModifyStart", "onUploadedGeometriesChange", "layerId", "features", "label", "boundaryType"];

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React, { Children, cloneElement, Fragment, useEffect, useMemo, useRef, useCallback } from 'react';
import { Feature } from 'ol';
import OlVectorSource from 'ol/source/Vector';
import Draw from 'ol/interaction/Draw';
import Modify from 'ol/interaction/Modify';
import Snap from 'ol/interaction/Snap';
import Point from 'ol/geom/Point';
import { Icon } from 'ol/style';
import Style, { createEditingStyle } from 'ol/style/Style';
import GeoJSON from 'ol/format/GeoJSON';
import { v4 as uuidv4 } from 'uuid';
import useMeasure from 'react-use-measure';
import { useOpenLayers } from '../openlayers-context';
import { getOptions } from '../utils';
import { Container } from './VectorSource.styles';
import { defaultMarkerStyle } from './MarkerStyle';
import getMeasurementStyles from '../../utils/getMeasurementStyles';
import { CLEAR_FEATURES_KEY } from '../../hooks/useClearFeatures';
import { useGeometryArea } from '../../hooks/useGeometryArea';
var allVectorSourceOptions = {
  attributions: undefined,
  url: undefined,
  wrapX: undefined
};

var VectorSource = _ref => {
  var {
    olLayer,
    canDraw,
    drawShape,
    canModify,
    drawStyle,
    allowMapMarker,
    markerStyle,
    markerCoordinates,
    measurementBaseStyles,
    measurementAreaStyle,
    measurementLineStyle,
    showLineMeasurements,
    showAreaMeasurements,
    canSnap,
    onChange,
    onDrawEnd,
    onDrawStart,
    onModifyEnd,
    onModifyStart,
    onUploadedGeometriesChange,
    layerId,
    features,
    label,
    boundaryType
  } = _ref,
      props = _objectWithoutProperties(_ref, _excluded);

  var {
    map,
    currentDrawInteractionRef,
    featureCollection
  } = useOpenLayers();
  var options = useMemo(() => getOptions(Object.assign(allVectorSourceOptions, props)), [props]);
  var source = useRef(new OlVectorSource(options));
  var drawFeatureInteraction = useRef(null);
  var snapFeatureInteraction = useRef(null);
  var modifyFeatureInteraction = useRef(null);
  var geometryAreaListRef = useRef([]);
  var [measureRef, viewportBounds] = useMeasure({
    polyfill: ResizeObserver
  });
  var featureMarkers = useRef(null);
  var condensed = !!(viewportBounds !== null && viewportBounds !== void 0 && viewportBounds.width) && viewportBounds.width < 900;
  var {
    getGeometryArea,
    getGeometryAreaWithSquareMetre
  } = useGeometryArea();
  useEffect(() => {
    if (map && olLayer) {
      olLayer.setSource(source.current);
    }

    return () => {
      var _olLayer$getSource;

      if (map && olLayer) (_olLayer$getSource = olLayer.getSource()) === null || _olLayer$getSource === void 0 ? void 0 : _olLayer$getSource.dispose();
    };
  }, [map, olLayer]); // Reset geometryAreaListRef on CLEAR_FEATURES_KEY change

  useEffect(() => {
    var handlePropertyChange = e => {
      var key = e.key;

      if (key === CLEAR_FEATURES_KEY && olLayer !== null && olLayer !== void 0 && olLayer.get(key)) {
        geometryAreaListRef.current = [];
        olLayer.set(CLEAR_FEATURES_KEY, false);
      }
    };

    olLayer === null || olLayer === void 0 ? void 0 : olLayer.on('propertychange', handlePropertyChange);
    return () => {
      olLayer === null || olLayer === void 0 ? void 0 : olLayer.un('propertychange', handlePropertyChange);
    };
  }, [olLayer]);

  var getGeoJSON = () => {
    var format = new GeoJSON();
    var sourceFeatures = source.current.getFeatures();
    var json = format.writeFeatures(sourceFeatures);
    return JSON.parse(json);
  };
  /**
   * When `canDraw` is true then `props.features` should be []
   * Draw interaction adds features to the current layer
   * When `canDraw` is false (ex:uploading file)
   * Then `props.features` should contain features[]
   *
   * Do not provide features when in draw mode
   * ***Undo functionality will not work***
   * As it will add the features in below effect right after removing it
   */


  useEffect(() => {
    if (features.length) {
      source.current.addFeatures(features);
      var sourceFeatures = source.current.getFeatures();
      var areas = sourceFeatures.map(feature => getGeometryArea(feature.getGeometry()));
      onUploadedGeometriesChange === null || onUploadedGeometriesChange === void 0 ? void 0 : onUploadedGeometriesChange({
        areas
      });
    }
  }, [features, getGeometryArea, onUploadedGeometriesChange]);
  var getStyles = useCallback(feature => {
    if ((showLineMeasurements || showAreaMeasurements) && measurementBaseStyles && measurementAreaStyle && measurementLineStyle) {
      var geometry = feature.getGeometry();
      return getMeasurementStyles({
        areaStyle: measurementAreaStyle,
        lineStyle: measurementLineStyle,
        showAreaMeasurements,
        showLineMeasurements,
        shapeStyle: measurementBaseStyles,
        geometry,
        getArea: geom => getGeometryAreaWithSquareMetre(geom)
      });
    } else {
      var _feature$getGeometry, _feature$getGeometry$;

      var drawingStyles = drawStyle !== null && drawStyle !== void 0 ? drawStyle : createEditingStyle();
      return drawingStyles[feature === null || feature === void 0 ? void 0 : (_feature$getGeometry = feature.getGeometry) === null || _feature$getGeometry === void 0 ? void 0 : (_feature$getGeometry$ = _feature$getGeometry.call(feature)) === null || _feature$getGeometry$ === void 0 ? void 0 : _feature$getGeometry$.getType()];
    }
  }, [drawStyle, getGeometryAreaWithSquareMetre, measurementAreaStyle, measurementBaseStyles, measurementLineStyle, showAreaMeasurements, showLineMeasurements]);
  /**
   * Resets layer featuestyles and
   * Defaults styles provided to interactions (ex: Draw, Modify)
   */

  var resetFeatureStyles = useCallback(() => {
    var layerFeatures = source.current.getFeatures();
    layerFeatures.length && layerFeatures.forEach(f => f.setStyle());
  }, []);
  var getGeometriesArea = useCallback(() => geometryAreaListRef.current.map(list => list.area), []);
  useEffect(() => {
    if (!map || !canDraw || !drawShape) return;
    var draw = new Draw({
      source: source.current,
      type: drawShape,
      style: getStyles,
      stopClick: true
    });
    drawFeatureInteraction.current = draw;
    map.addInteraction(draw);
    draw.on('drawabort', function () {
      if (currentDrawInteractionRef) {
        currentDrawInteractionRef.current = null;
        var onDrawEndArgs = {
          areaMeasurements: getGeometriesArea()
        };
        onDrawEnd === null || onDrawEnd === void 0 ? void 0 : onDrawEnd(onDrawEndArgs);
      }
    });
    draw.on('drawstart', function (e) {
      onDrawStart === null || onDrawStart === void 0 ? void 0 : onDrawStart({
        feature: e.feature
      }); // Remove any validation styles on draw start

      resetFeatureStyles();

      if (currentDrawInteractionRef) {
        currentDrawInteractionRef.current = draw;
      }
    });
    draw.on('drawend', function (e) {
      e.feature.setId(uuidv4()); // Set unique ID for each drawn feature

      e.feature.set('reference', layerId);
      boundaryType && e.feature.set('boundaryType', boundaryType);
      var onDrawEndArgs = {};

      if (currentDrawInteractionRef) {
        currentDrawInteractionRef.current = null;
      }

      var geometry = e.feature.getGeometry();

      if (geometry) {
        var area = getGeometryArea(geometry);
        geometryAreaListRef.current.push({
          id: e.feature.getId(),
          area: area
        });
        onDrawEndArgs = {
          areaMeasurements: getGeometriesArea()
        };
      }

      onDrawEnd === null || onDrawEnd === void 0 ? void 0 : onDrawEnd(onDrawEndArgs);
      source.current.once('change', () => {
        onChange === null || onChange === void 0 ? void 0 : onChange(getGeoJSON());
      });
    });
    return function () {
      map.removeInteraction(draw);
      drawFeatureInteraction.current = null;
    };
  }, [boundaryType, canDraw, currentDrawInteractionRef, drawShape, getGeometriesArea, getGeometryArea, getStyles, label, layerId, map, onChange, onDrawEnd, onDrawStart, resetFeatureStyles]);
  useEffect(() => {
    if (!map || !canModify) return;
    var modify = new Modify({
      source: source.current,
      style: getStyles
    });
    modifyFeatureInteraction.current = modify;
    map.addInteraction(modify);
    modify.on('modifystart', function (e) {
      if (onModifyStart) {
        var thisFeature;
        e.features.forEach(feature => {
          var geometry = feature.getGeometry();

          if (geometry !== undefined && geometry.getType() === 'Polygon') {
            thisFeature = feature;
          }
        });

        if (thisFeature) {
          onModifyStart({
            feature: thisFeature
          });
        }
      }
    });
    modify.on('modifyend', function () {
      if (onModifyEnd) {
        var existingFeatures = source.current.getFeatures();
        existingFeatures.forEach(feature => {
          var geometry = feature.getGeometry();
          var featureId = feature.getId();
          var filteredGeometryAreas = geometryAreaListRef.current.filter(list => list.id !== featureId);

          if ((geometry === null || geometry === void 0 ? void 0 : geometry.getType()) === 'Polygon') {
            var area = getGeometryArea(geometry);
            filteredGeometryAreas.push({
              id: featureId,
              area: area
            });
            geometryAreaListRef.current = filteredGeometryAreas;
          }
        });
        onModifyEnd({
          areaMeasurements: getGeometriesArea()
        });
      } // Remove any validation styles on modify end


      resetFeatureStyles();
      onChange === null || onChange === void 0 ? void 0 : onChange(getGeoJSON());
    });
    return function () {
      map.removeInteraction(modify);
      modifyFeatureInteraction.current = null;
    };
  }, [canModify, getGeometriesArea, getGeometryArea, getStyles, map, onChange, onModifyEnd, onModifyStart, resetFeatureStyles]);
  useEffect(() => {
    if (!map || !canSnap) return;
    var snap = new Snap({
      features: featureCollection === null || featureCollection === void 0 ? void 0 : featureCollection.current
    });
    snapFeatureInteraction.current = snap;
    map.addInteraction(snap);
    return function () {
      map.removeInteraction(snap);
      snapFeatureInteraction.current = null;
    };
  }, [canSnap, canDraw, canModify, featureCollection, map, onModifyEnd]);
  useEffect(() => {
    var _ref2, _marker$mobileScale;

    if (!source) return;
    var marker = (_ref2 = markerStyle ? markerStyle() : undefined) !== null && _ref2 !== void 0 ? _ref2 : defaultMarkerStyle();
    if (!marker) return;
    var iconStyle = new Style({
      image: new Icon({
        anchorXUnits: 'pixels',
        anchorYUnits: 'pixels',
        color: 'transparent',
        anchor: [marker.anchorX === undefined ? marker.width / 2 : marker.anchorX, marker.anchorY === undefined ? marker.height / 2 : marker.anchorY],
        scale: condensed ? (_marker$mobileScale = marker.mobileScale) !== null && _marker$mobileScale !== void 0 ? _marker$mobileScale : 1 : 1,
        size: [marker.width, marker.height],
        imgSize: [marker.width, marker.height],
        src: marker.markerImage
      })
    });
    featureMarkers.current = allowMapMarker && markerCoordinates ? markerCoordinates.map(x => {
      var feature = new Feature(new Point(x));
      feature.setStyle(iconStyle);
      source.current.addFeature(feature);
      return feature;
    }) : null;
    var previousSource = source.current;
    return function () {
      if (featureMarkers.current) {
        featureMarkers.current.forEach(feature => previousSource.removeFeature(feature));
      }
    };
  }, [allowMapMarker, condensed, markerCoordinates, markerStyle]);
  return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(Container, {
    ref: measureRef
  }), Children.map(props.children, child => child && /*#__PURE__*/cloneElement(child, {
    source: source.current
  })));
};

export default VectorSource;